import React from 'react';
import {ListGroup, Button, Stack, Badge} from 'react-bootstrap';
import {Link} from 'react-router-dom';
import {VscSparkleFilled} from 'react-icons/vsc';
import {
  FaFilePdf,
  FaCheckCircle,
  FaLinkedin,
  FaPlusCircle,
  FaExclamationCircle,
} from 'react-icons/fa';
import {SiIndeed} from 'react-icons/si';
import {useAppSelector} from '../store/hooks';

type QuickStartProps = {
  isAuthenticated: boolean;
  hasResume?: boolean;
  hasJob?: boolean;
  onOpenImportPlatformsModal?: () => void;
};

export default function QuickStart({
  isAuthenticated,
  hasResume,
  hasJob,
  onOpenImportPlatformsModal,
}: QuickStartProps): React.JSX.Element {
  const Step = ({
    number,
    title,
    desc,
    done,
  }: {
    number: number;
    title: string;
    desc: string;
    done?: boolean;
  }) => (
    <ListGroup.Item className='d-flex align-items-start'>
      <div style={{minWidth: 24, fontWeight: 600}} aria-hidden>
        {number}.
      </div>
      <div className='ms-2' style={{flex: 1}}>
        <div className='d-flex align-items-center'>
          <strong>{title}</strong>
          {isAuthenticated ? (
            done ? (
              <Badge
                bg='success'
                className='ms-2 d-inline-flex align-items-center'
              >
                <FaCheckCircle className='me-1' /> Done
              </Badge>
            ) : (
              <Badge
                bg='danger'
                className='ms-2 d-inline-flex align-items-center'
              >
                <FaExclamationCircle className='me-1' /> Needed
              </Badge>
            )
          ) : null}
        </div>
        <div className='text-muted'>{desc}</div>
      </div>
    </ListGroup.Item>
  );

  // Combine server-aware props (from Home) with local quickstart progress persisted in Redux/localStorage
  const qs = useAppSelector(s => s.quickstart);
  const effectiveHasResume = Boolean(hasResume) || Boolean(qs?.hasResume);
  const effectiveHasJob = Boolean(hasJob) || Boolean(qs?.hasJob);
  const effectiveHasTailored = Boolean(qs?.hasTailored);
  const effectiveHasExported = Boolean(qs?.hasExported);

  return (
    <div>
      <ListGroup variant='flush' className='mb-3'>
        <Step
          number={1}
          title='Add your resume'
          desc='Create a resume or import a PDF (LinkedIn/Indeed supported).'
          done={effectiveHasResume}
        />
        <Step
          number={2}
          title='Add a job'
          desc='Paste a job posting or generate a job with AI.'
          done={effectiveHasJob}
        />
        <Step
          number={3}
          title='Tailor with AI'
          desc="Open the Jobs page, select a job, and click the 'Generate Resume' button on that job to create a draft tailored to the job. Review and edit the result before saving."
          done={effectiveHasTailored}
        />
        <Step
          number={4}
          title='Export to PDF'
          desc='Download a clean, ATS-friendly PDF for your application.'
          done={effectiveHasExported}
        />
      </ListGroup>

      {isAuthenticated ? null : (
        <div className='small text-muted mb-2'>
          Note: You’ll be asked to sign up or log in before saving your work.
        </div>
      )}

      <Stack direction='horizontal' gap={2} className='flex-wrap'>
        <Link
          to='/resumes/new'
          className='btn btn-outline-secondary btn-sm icon-btn'
        >
          <FaPlusCircle className='me-1' /> Create Resume
        </Link>
        <Link
          to='/resumes/import-pdf'
          className='btn btn-outline-secondary btn-sm d-inline-flex align-items-center'
        >
          <FaFilePdf className='me-1' /> Import PDF
        </Link>
        <Button
          variant='outline-secondary'
          size='sm'
          className='d-inline-flex align-items-center'
          onClick={onOpenImportPlatformsModal}
        >
          Import from{' '}
          <FaLinkedin className='ms-1 me-1' style={{color: '#0a66c2'}} />{' '}
          <SiIndeed style={{color: '#2557a7'}} />
        </Button>
        <Link
          to='/jobs'
          className='btn btn-outline-primary btn-sm d-inline-flex align-items-center'
        >
          <VscSparkleFilled className='me-1' /> Open Jobs — Generate Resume
        </Link>
        <div className='vr d-none d-md-block' />
        <Link
          to='/jobs/new'
          className='btn btn-outline-secondary btn-sm icon-btn'
        >
          <FaPlusCircle className='me-1' /> Create Job
        </Link>
        <Link
          to='/resumes/ai'
          className='btn btn-secondary btn-sm d-inline-flex align-items-center'
        >
          <VscSparkleFilled className='me-1' /> AI Resume
        </Link>
        <Link
          to='/jobs/ai'
          className='btn btn-secondary btn-sm d-inline-flex align-items-center'
        >
          <VscSparkleFilled className='me-1' /> AI Job
        </Link>
      </Stack>

      <div className='small text-muted mt-2'>
        Tip: This takes about 2 minutes. Always review AI output for accuracy.
      </div>
    </div>
  );
}
